'use client';

import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';
import { batch } from '@legendapp/state';
import { type HeroBlockSearchFormTriggerId, useHeroBlockSearchFormState } from '@/components/forms/HeroBlockSearchForm/HeroBlockSearchFormProvider';
import { cn } from '@/lib/cn';
import analytics from '@/services/analytics';
import type { AnalyticsInteractionEventParams } from '@/services/analytics/plugins/analyticsJucyInteraction';

export type HeroSearchFormMobileSearchBtnProps = {
    isPrimaryButton?: boolean;
    className?: string;
    id?: string;
    style?: CSSProperties;
    children?: ReactNode;
    openStep?: HeroBlockSearchFormTriggerId;
    trackInteraction?: AnalyticsInteractionEventParams;
};

export function HeroSearchFormMobileSearchBtn({ isPrimaryButton, id, className, style, openStep, children, trackInteraction }: HeroSearchFormMobileSearchBtnProps) {
    const btnRef = useRef<HTMLButtonElement>(null);
    const { triggerId$, open$, showStickySearchBtn$ } = useHeroBlockSearchFormState();

    useEffect(() => {
        const btn = btnRef.current;
        if (!btn || !isPrimaryButton) {
            return;
        }
        const observer = new window.IntersectionObserver(
            ([entry]) => {
                showStickySearchBtn$.set(!entry.isIntersecting);
            },
            { threshold: 0 }
        );
        observer.observe(btn);
        return () => observer.disconnect();
    }, [isPrimaryButton, showStickySearchBtn$]);

    return (
        <button
            ref={btnRef}
            id={id}
            className={cn('btn btn-primary', className)}
            style={style}
            onClick={(e) => {
                analytics.plugins.interactions.trackInteraction({
                    source: trackInteraction?.source || 'jucy-website',
                    category: trackInteraction?.category || 'book-now-cta',
                    action: trackInteraction?.action || 'click',
                    label: trackInteraction?.label || e.currentTarget.textContent || '',
                });
                batch(() => {
                    triggerId$.set(openStep || 'fleet-type');
                    open$.set(true);
                });
            }}
        >
            {children}
        </button>
    );
}
